import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

interface GetComplimentaryCouponHistoryParams {
  userId?:     string;
  serviceType?: string;
  branchId?:   string;
  search?:     string;
  page?:       number;
  limit?:      number;
}

interface GetComplimentaryCouponsParams extends GetComplimentaryCouponHistoryParams {
  status?: 'pending' | 'live' | 'completed';
}

interface StartComplementaryServiceArgs {
  membershipId: string;
  couponId:     string;
  sessionId:    string;
}

interface AssignComplementaryTrainerArgs {
  membershipId: string;
  serviceType:  string;
  coachId:      string;
}

interface UnassignComplementaryTrainerArgs {
  membershipId: string;
  serviceType:  string;
}

interface AssignComplementaryTrainerSessionArgs {
  membershipId: string;
  couponId:     string;
  sessionId:    string;
  coachId:      string;
}

interface UnassignComplementaryTrainerSessionArgs {
  membershipId: string;
  couponId:     string;
  sessionId:    string;
}

interface UpdateComplementaryServiceArgs {
  membershipId: string;
  serviceType:  string;
  body: {
    serviceType?:   string;
    totalSessions?: number;
  };
}

export const complimentaryCoupon = createApi({
  reducerPath: 'complimentaryCoupon',
  baseQuery:   dynamicBaseQuery,
  tagTypes:    ['ComplimentaryCoupon'] as const,
  endpoints:   (builder) => ({
    getComplimentaryCouponHistory: builder.query<unknown, GetComplimentaryCouponHistoryParams | void>({
      query: (params) => ({
        url:    '/memberships/complimentary-coupons/sessions-history',
        method: 'GET',
        params: params || undefined,
      }),
      providesTags: ['ComplimentaryCoupon'],
    }),
    getComplimentaryCouponsSummary: builder.query<unknown, GetComplimentaryCouponHistoryParams | void>({
      query: (params) => ({
        url:    '/memberships/complimentary-coupons/history',
        method: 'GET',
        params: params || undefined,
      }),
      providesTags: ['ComplimentaryCoupon'],
    }),
    getComplimentaryCoupons: builder.query<unknown, GetComplimentaryCouponsParams | void>({
      query: (params) => ({
        url:    '/memberships/complimentary-coupons',
        method: 'GET',
        params: params || undefined,
      }),
      providesTags: ['ComplimentaryCoupon'],
    }),
    getComplementaryServices: builder.query<unknown, string>({
      query: (membershipId) => ({
        url:    `/memberships/${membershipId}/complementary-services`,
        method: 'GET',
      }),
      providesTags: ['ComplimentaryCoupon'],
    }),
    startComplementaryService: builder.mutation<unknown, StartComplementaryServiceArgs>({
      query: ({ membershipId, couponId, sessionId }) => ({
        url:    `/memberships/${membershipId}/complementary-services/${couponId}/sessions/${sessionId}/start`,
        method: 'PATCH',
      }),
      invalidatesTags: ['ComplimentaryCoupon'],
    }),
    assignComplementaryTrainer: builder.mutation<unknown, AssignComplementaryTrainerArgs>({
      query: ({ membershipId, serviceType, coachId }) => ({
        url:    `/memberships/${membershipId}/complementary-services/${serviceType}/assign-trainer`,
        method: 'PATCH',
        body:   { coachId },
      }),
      invalidatesTags: ['ComplimentaryCoupon'],
    }),
    unassignComplementaryTrainer: builder.mutation<unknown, UnassignComplementaryTrainerArgs>({
      query: ({ membershipId, serviceType }) => ({
        url:    `/memberships/${membershipId}/complementary-services/${serviceType}/unassign-trainer`,
        method: 'PATCH',
      }),
      invalidatesTags: ['ComplimentaryCoupon'],
    }),
    assignComplementaryTrainerSession: builder.mutation<unknown, AssignComplementaryTrainerSessionArgs>({
      query: ({ membershipId, couponId, sessionId, coachId }) => ({
        url:    `/memberships/${membershipId}/complementary-services/${couponId}/sessions/${sessionId}/assign-trainer`,
        method: 'PATCH',
        body:   { coachId },
      }),
      invalidatesTags: ['ComplimentaryCoupon'],
    }),
    unassignComplementaryTrainerSession: builder.mutation<unknown, UnassignComplementaryTrainerSessionArgs>({
      query: ({ membershipId, couponId, sessionId }) => ({
        url:    `/memberships/${membershipId}/complementary-services/${couponId}/sessions/${sessionId}/unassign-trainer`,
        method: 'PATCH',
      }),
      invalidatesTags: ['ComplimentaryCoupon'],
    }),
    updateComplementaryService: builder.mutation<unknown, UpdateComplementaryServiceArgs>({
      query: ({ membershipId, serviceType, body }) => ({
        url:    `/memberships/${membershipId}/complementary-services/${serviceType}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ComplimentaryCoupon'],
    }),
  }),
});

export const {
  useGetComplimentaryCouponHistoryQuery,
  useGetComplimentaryCouponsSummaryQuery,
  useGetComplimentaryCouponsQuery,
  useGetComplementaryServicesQuery,
  useStartComplementaryServiceMutation,
  useUpdateComplementaryServiceMutation,
  useAssignComplementaryTrainerMutation,
  useUnassignComplementaryTrainerMutation,
  useAssignComplementaryTrainerSessionMutation,
  useUnassignComplementaryTrainerSessionMutation,
} = complimentaryCoupon;
